<template>
  <b-overlay
    :show="show"
    rounded="lg"
  >
    <b-card
      :img-src="desktop.image ? `..${desktop.image.url}` : ''"
      class="border-0 mx-3"
      :aria-hidden="show ? 'true' : null"
      img-alt=""
      img-top
      no-body
    >
      <vue-fab
        v-if="(desktop.type === 'persistent' && !(!desktop.editable && desktop.permissions.length < 1)) || (getConfig.canUseBastion && bastion && (bastion.http.enabled || bastion.ssh.enabled))"
        icon="more_vert"
        main-btn-color="#bcc6cc"
        class="info-icon position-absolute"
        size="small"
        unfold-direction="down"
        :scroll-auto-hide="false"
        active-icon="more_horiz"
      >
        <fab-item
          v-if="getConfig.canUseBastion && bastion && (bastion.http.enabled || bastion.ssh.enabled)"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.bastion')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="desktop.editable ? getUser.role_id != 'user' ? (desktop.needsBooking && !desktop.tag) ? 5 : 4 : 2 : (desktop.permissions.length)"
          icon="fort"
          color="#303446"
          @clickItem="onClickShowBastionModal(bastion)"
        />

        <fab-item
          v-if="desktop.permissions.includes('recreate')"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.recreate')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="desktop.editable ? 4 : desktop.permissions.indexOf('recreate')"
          icon="replay"
          color="#f3bc65"
          @clickItem="onClickRecreateDesktop({id: desktop.id, name: desktop.name})"
        />

        <fab-item
          v-if="desktop.editable && desktop.needsBooking && !desktop.tag"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.booking')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="getUser.role_id != 'user' ? 4 : 2"
          icon="event"
          color="#eead47"
          @clickItem="onClickBookingDesktop(desktop)"
        />
        <fab-item
          v-if="desktop.editable && getUser.role_id != 'user'"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.direct-link')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="3"
          icon="link"
          color="#ab3ed1"
          @clickItem="onClickOpenDirectViewerModal({itemId: desktop.id})"
        />
        <fab-item
          v-if="desktop.editable && getUser.role_id != 'user' && desktop.type === 'persistent'"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.template')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="2"
          color="#97c277"
          @clickItem="onClickGoToNewTemplate(desktop.id)"
        >
          <font-awesome-icon
            :icon="['fas', 'cubes']"
            class="ml-1 mt-1 text-white"
          />
        </fab-item>
        <fab-item
          v-if="desktop.editable && desktop.type === 'persistent'"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.delete')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="1"
          icon="close"
          color="#e34934"
          @clickItem="onClickDeleteDesktop"
        />
        <fab-item
          v-if="desktop.editable"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.edit')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="0"
          icon="edit"
          color="#318bb5"
          @clickItem="onClickGoToEditDesktop({itemId: desktop.id, returnPage: currentRouteName})"
        />
        <fab-item
          v-if="desktop.editable && getUser.role_id != 'user' && desktop.type === 'persistent' && desktop.storage && desktop.storage.length"
          v-b-tooltip="{ title: `${$t('components.desktop-cards.actions.increase-storage')}`,
                         placement: 'right',
                         customClass: 'isard-tooltip',
                         trigger: 'hover' }"
          :idx="increaseStorageIdx"
          icon="storage"
          color="#13bf9d"
          @clickItem="onClickIncreaseStorage"
        />
      </vue-fab>

      <!-- Desktop next booking -->
      <div
        v-if="desktop.needsBooking || templateNeedsBooking"
        class="machine-notification-bar px-3 d-flex flex-row align-content-center text-white notification-bar"
        :class="notificationBarCssClass"
      >
        <p
          v-b-tooltip.hover="{ title: `${bookingNotificationText.length > MAX_BOOKING_TEXT_SIZE ? bookingNotificationText : '' }` , placement: 'top', customClass: 'isard-tooltip', trigger: 'hover' }"
          class="mb-0 py-2 text-white text-truncate"
          :title="`${bookingNotificationText.length > MAX_BOOKING_TEXT_SIZE ? bookingNotificationText : '' }`"
        >
          {{ bookingNotificationText }}
        </p>
      </div>
      <!-- Desktop next booking -->
      <div
        v-else-if="desktop.shutdown"
        class="machine-notification-bar px-4 d-flex flex-row align-content-center text-white bg-dark notification-bar"
      >
        <p class="mb-0 py-2 text-white">
          {{ desktop.shutdown }}
        </p>
      </div>

      <div
        class="p-2 h-100 d-flex flex-wrap flex-column"
        :class="`${desktop.needsBooking || templateNeedsBooking || desktop.shutdown ? 'notification-bar' : '' } getCardBackgroundColor` "
      >
        <div class="flex-grow-1">
          <!-- Title -->
          <div
            v-b-tooltip="{ title: `${getCardTitle.length > MAX_TITLE_SIZE ? getCardTitle : ''}`, placement: 'top', customClass: 'isard-tooltip', trigger: 'hover' }"
            class="font-weight-bold card-title ml-2 mt-2 mb-2"
          >
            {{ getCardTitle | truncate(MAX_TITLE_SIZE) }}
          </div>

          <!-- DESCRIPTION -->
          <p
            v-b-tooltip="{ title: `${getCardDescription.length > MAX_DESCRIPTION_SIZE ? getCardDescription : ''}`, placement: 'top', customClass: 'isard-tooltip', trigger: 'hover' }"
            class="w-100 mb-0 card-text ml-2 mb-2"
          >
            {{ getCardDescription | truncate(MAX_DESCRIPTION_SIZE) }}
          </p>

          <!-- State -->
          <div
            v-b-tooltip="{title: `${desktop.currentAction ? $t('components.desktop-cards.storage-operation.'+desktop.currentAction, { action: desktop.currentAction} ) : ''}`}"
            class="ml-4 d-flex flex-row justify-left"
          >
            <b-spinner
              v-if="[desktopStates.downloading, desktopStates.waitingip, desktopStates.working, desktopStates['shutting-down'], desktopStates.maintenance, desktopStates.unknown, desktopStates.verifying].includes(desktopState.toLowerCase())"
              small
              variant="light"
              class="align-self-center mr-2 status-spinner"
            />
            <span
              class="d-flex flex-column justify-content-center"
            >
              <p
                class="mb-0 text-state font-weight-bold"
                :class="statusTextCssColor"
              >
                {{ desktop.type === 'nonpersistent' && desktopState === desktopStates.stopped ? $t(`views.select-template.status.readyCreation.text`) : $t(`views.select-template.status.${desktopState}.text`) }}
              </p>
              <p
                v-if="
                  [desktopStates.downloading, desktopStates.waitingip, desktopStates.working, desktopStates['shutting-down'], desktopStates.maintenance].includes(desktopState.toLowerCase())
                    && desktop.queue && ![0].includes(queuePosition)
                "
                class="mb-0 text-state-small font-weight-bold"
                :class="statusTextCssColor"
              >
                {{ $t('components.desktop-cards.queue', { position: queuePosition }) }}
              </p>
            </span>
          </div>
          <div
            v-if="desktop.currentAction && desktopState == desktopStates.maintenance"

            class="my-4 mx-2 card-text text-warning"
          >
            {{ $t('components.desktop-cards.storage-operation.'+desktop.currentAction, { action: desktop.currentAction} ) }}
          </div>

          <div
            v-if="desktop.progress"
            class="ml-4 mr-4 flex-row justify-left"
          >
            <small>{{ desktop.progress.size }} - {{ desktop.progress.throughput_average }}/s - {{ desktop.progress.time_left }}</small>
            <b-progress
              :max="100"
              height="2rem"
            >
              <b-progress-bar
                variant="secondary"
                :value="desktop.progress.percentage"
                show-progress
                animated
              >
                <strong>{{ desktop.progress.percentage }} %</strong>
              </b-progress-bar>
            </b-progress>
          </div>

          <!-- Actions -->
          <div
            v-if="[desktopStates.started, desktopStates.waitingip, desktopStates.stopped, desktopStates.failed, desktopStates['shutting-down'], desktopStates.paused, desktopStates.maintenance].includes(desktopState)"
            class="d-flex flex-row justify-content-start ml-3 mb-1"
          >
            <!-- Main action button nonpersistent -->
            <DesktopButton
              v-if="!desktop.state"
              class="card-button"
              :active="true"
              :button-class="buttCssColor"
              :spinner-active="false"
              :butt-text="$t('views.select-template.status.notCreated.action')"
              :icon-name="desktop.buttonIconName"
              @buttonClicked="chooseDesktop(desktop)"
            />
            <!-- Main action button persistent-->
            <DesktopButton
              v-else-if="desktop.type === 'persistent' || (desktop.type === 'nonpersistent' && desktop.state)"
              class="card-button"
              :active="!isPendingOperation(desktop.id)"
              :button-class="buttCssColor"
              :spinner-active="isPendingOperation(desktop.id)"
              :butt-text="$t(`views.select-template.status.${desktopState}.action`)"
              :icon-name="desktop.buttonIconName"
              @buttonClicked="changeDesktopStatus(desktop, { action: status[desktopState || 'stopped'].action, desktopId: desktop.id, storage: desktop.storage })"
            />
            <!-- Delete action button-->
            <DesktopButton
              v-if="desktop.state && desktop.type === 'nonpersistent' && desktopState === desktopStates.stopped"
              class="card-button"
              :active="true"
              button-class="btn-red"
              :spinner-active="false"
              :butt-text="$t('views.select-template.remove')"
              icon-name="x"
              @buttonClicked="deleteNonpersistentDesktop(desktop.id)"
            />
          </div>

          <!-- IP -->
          <p class="w-100 mb-0 card-text ml-2 mt-2 mb-1">
            {{ desktop.ip ? `IP:  ${desktop.ip}` : '' }}
          </p>

          <!-- Template -->
          <p
            v-if="desktop.type === 'persistent' && template"
            v-b-tooltip="{ title: `${template.name.length > MAX_TEMPLATE_TEXT_SIZE ? template.name : ''}`, placement: 'top', customClass: 'isard-tooltip', trigger: 'hover' }"
            class="w-100 mb-0 card-text ml-2"
          >
            {{ $t('forms.new-desktop.desktop-template') }}: {{ template.name | truncate(MAX_TEMPLATE_TEXT_SIZE) }}
          </p>
        </div>

        <!-- Actions -->
        <div
          v-if="[desktopStates.started, desktopStates.waitingip, desktopStates['shutting-down']].includes(desktopState)"
          class="d-flex flex-column mb-2 buttons-padding buttons-margin"
        >
          <div class="ml-1 mb-1">
            {{ $t("components.desktop-cards.open-with") }}:
          </div>
          <div>
            <DesktopButton
              v-if="!hideViewers && desktop.viewers && desktop.viewers.length === 1"
              :active="desktopState === desktopStates.started || (desktopState === desktopStates.waitingip && !singleViewerNeedsIp)"
              :button-class="!singleViewerNeedsIp ? 'btn-green' : buttViewerCssColor"
              :butt-text="getViewerText"
              variant="primary"
              :spinner-active="waitingIp"
              @buttonClicked="openDesktop({desktopId: desktop.id, viewer: desktop.viewers[0]})"
            />
            <isard-dropdown
              v-if="!hideViewers && desktop.viewers && desktop.viewers.length > 1"
              :dd-disabled="!showDropDown"
              css-class="viewers-dropdown m-0"
              :class="{ 'dropdown-inactive': !showDropDown, 'dropdown-wait': viewerNeedsIp(getDefaultViewer) && (isWaiting(desktop.interfaces) || waitingIp), 'dropdown-active': (!viewerNeedsIp(getDefaultViewer) || !isWaiting(desktop.interfaces) && !waitingIp) }"
              variant="light"
              :viewers="filterViewerFromList"
              :desktop="desktop"
              :viewer-text="getViewerText.substring(0, MAX_VIEWER_TEXT_SIZE)"
              :full-viewer-text="getViewerText"
              :default-viewer="getDefaultViewer"
              :waiting-ip="waitingIp"
              @dropdownClicked="openDesktop"
              @notifyWaitingIp="notifyWaitingIp"
            />
          </div>
        </div>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import i18n from '@/i18n'
import { DesktopUtils } from '@/utils/desktopsUtils'
import { ErrorUtils } from '@/utils/errorUtils'
import { mapActions, mapGetters } from 'vuex'
import IsardDropdown from '@/components/shared/IsardDropdown.vue'
import DesktopButton from '@/components/desktops/Button.vue'
import { desktopStates, status } from '@/shared/constants'
import { DateUtils } from '@/utils/dateUtils'

const MAX_TITLE_SIZE = 20
const MAX_DESCRIPTION_SIZE = 30
const MAX_TEMPLATE_TEXT_SIZE = 16
const MAX_VIEWER_TEXT_SIZE = 40
const MAX_BOOKING_TEXT_SIZE = 30

export default {
  components: { DesktopButton, IsardDropdown },
  props: {
    desktop: Object,
    templates: {
      required: true,
      type: Array
    }
  },
  setup (_, context) {
    const $store = context.root.$store

    const changeDesktopStatus = (desktop, data) => {
      if (data.action === 'cancel') {
        $store.dispatch('cancelOperation', data)
      } else if (canStart(desktop) || data.action !== 'start') {
        $store.dispatch('changeDesktopStatus', data)
      } else {
        $store.dispatch('checkCanStart', { id: desktop.id, type: 'desktop', profiles: desktop.reservables.vgpus, action: data.action })
      }
    }

    const canStart = (desktop) => {
      if (desktop.needsBooking) {
        return desktop.bookingId
      } else {
        return true
      }
    }

    const onClickBookingDesktop = (desktop) => {
      const data = { id: desktop.id, type: 'desktop', name: desktop.name }
      $store.dispatch('goToItemBooking', data)
    }

    const notifyWaitingIp = () => {
      $store.dispatch('showNotification', { message: i18n.t('messages.info.warning-desktop-waiting-ip') })
    }
    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text)
      $store.dispatch('showNotification', { message: i18n.t('forms.domain.bastion.copied') })
    }

    return {
      changeDesktopStatus,
      onClickBookingDesktop,
      notifyWaitingIp,
      copyToClipboard
    }
  },
  data () {
    return {
      desktopStates,
      status,
      item: {},
      highlightDropdown: false,
      MAX_TITLE_SIZE,
      MAX_DESCRIPTION_SIZE,
      MAX_TEMPLATE_TEXT_SIZE,
      MAX_VIEWER_TEXT_SIZE,
      MAX_BOOKING_TEXT_SIZE,
      show: false
    }
  },
  computed: {
    ...mapGetters(['getViewers', 'getCurrentTab', 'getUser', 'getConfig', 'isPendingOperation', 'getStorage']),
    filterViewerFromList () {
      return DesktopUtils.filterViewerFromList(this.desktop.viewers, this.getDefaultViewer)
    },
    templateNeedsBooking () {
      return DesktopUtils.templateNeedsBooking(this.desktop)
    },
    bookingNotificationText () {
      if (this.templateNeedsBooking) {
        return i18n.t('components.desktop-cards.notification-bar.no-next-booking')
      }
      return this.getBookingNotificationBar(
        this.desktop.nextBookingStart,
        this.desktop.nextBookingEnd
      )
    },
    notificationBarCssClass () {
      const states = {
        stopped: 'state-off',
        started: 'state-on',
        waitingip: 'state-loading',
        error: 'state-error',
        failed: 'state-failed',
        working: 'state-loading',
        'shutting-down': 'state-loading',
        booking: 'booking-notification'
      }
      return states[
        this.desktop.needsBooking || this.templateNeedsBooking
          ? 'booking'
          : this.desktopState
      ]
    },
    buttCssColor () {
      const stateColors = {
        stopped: 'btn-green',
        'shutting-down': 'btn-red',
        started: 'btn-red',
        waitingip: 'btn-red',
        failed: 'btn-orange',
        paused: 'btn-red',
        maintenance: 'btn-red'
      }
      return stateColors[this.desktopState]
    },
    buttViewerCssColor () {
      const stateColors = {
        stopped: 'btn-gray',
        started: 'btn-green',
        waitingip: 'btn-orange',
        failed: 'btn-red'
      }
      return stateColors[this.desktopState]
    },
    statusTextCssColor () {
      if (this.desktop.type === 'nonpersistent' && this.desktopState === desktopStates.stopped) {
        return 'status-green'
      }

      const stateColors = {
        stopped: 'status-gray',
        started: 'status-green',
        waitingip: 'status-orange',
        failed: 'status-red',
        'shutting-down': 'status-orange',
        working: 'status-orange',
        downloading: 'status-orange',
        paused: 'status-red',
        maintenance: 'status-orange',
        unknown: 'status-orange'
      }
      return stateColors[this.desktopState]
    },
    getCardBackgroundColor () {
      if (this.desktop.server) {
        return 'serverHighlight'
      } else if (this.desktopState === desktopStates.started) {
        return 'startedHighlight'
      } else {
        return ''
      }
    },
    desktopState () {
      return (this.desktop.state && this.desktop.state.toLowerCase()) || desktopStates.stopped
    },
    waitingIp () {
      return this.desktopState === desktopStates.waitingip
    },
    showDropDown () {
      return [desktopStates.started, desktopStates.waitingip, desktopStates['shutting-down']].includes(this.desktopState)
    },
    getViewerText () {
      return this.getDefaultViewer !== '' ? i18n.t(`views.select-template.viewer-name.${this.getDefaultViewer}`) : i18n.t('views.select-template.viewers')
    },
    hideViewers () {
      return this.desktop.state && this.desktop.type === 'nonpersistent' && this.desktopState === desktopStates.stopped
    },
    template () {
      // find, not filter: this runs once per card over the full template list
      return (this.desktop.template && this.templates.find(template => template.id === this.desktop.template)) || null
    },
    bastion () {
      const bastion = this.$store.getters.getBastionTargets.find(b => b.desktop_id === this.desktop.id)
      return bastion || null
    },
    bastionVisible () {
      return this.getConfig.canUseBastion && this.bastion && (this.bastion.http.enabled || this.bastion.ssh.enabled)
    },
    increaseStorageIdx () {
      // Sits above edit(0)/delete(1)/template(2)/direct-link(3); the next slot is shared by booking/recreate, then bastion.
      let idx = 4
      if ((this.desktop.needsBooking && !this.desktop.tag) || this.desktop.permissions.includes('recreate')) idx += 1
      if (this.bastionVisible) idx += 1
      return idx
    },
    getCardTitle () {
      return this.desktop.type === 'persistent' || (!this.desktop.state && this.desktop.type === 'nonpersistent' && this.desktopState === desktopStates.stopped) ? this.desktop.name : this.template && this.template.name
    },
    getCardDescription () {
      return (this.desktop.description !== null && this.desktop.description !== undefined) ? this.desktop.description : ''
    },
    getDefaultViewer () {
      if (this.desktop.viewers !== undefined) {
        if (this.getViewers[this.desktop.id] !== undefined && this.desktop.viewers.includes(this.getViewers[this.desktop.id])) {
          return this.getViewers[this.desktop.id]
        } else if (this.desktop.viewers.length > 0) {
          return this.desktop.viewers.includes('browser') ? 'browser' : this.desktop.viewers[0]
        }
      }
      return ''
    },
    singleViewerNeedsIp () {
      if (!this.desktop.viewers || !this.desktop.viewers.length > 0) {
        return false
      }
      return DesktopUtils.viewerNeedsIp(this.desktop.viewers[0])
    },
    currentRouteName () {
      return this.$route.name
    },
    queuePosition () {
      return this.desktop.queue || 0
    }
  },
  destroyed () {
    this.$snotify.clear()
  },
  methods: {
    ...mapActions([
      'deleteDesktop',
      'deleteNonpersistentDesktop',
      'openDesktop',
      'createDesktop',
      'checkCanStartNewNonpersistent',
      'navigate',
      'goToEditDomain',
      'fetchDirectLink',
      'goToNewTemplate',
      'updateDesktopModal',
      'recreateDesktop',
      'bastionModalShow',
      'showIncreaseModal'
    ]),
    getBookingNotificationBar (dateStart, dateEnd) {
      if (DateUtils.dateIsAfter(dateEnd, new Date()) && DateUtils.dateIsBefore(dateStart, new Date())) {
        return i18n.t('components.desktop-cards.notification-bar.booking-ends') + DateUtils.formatAsTime(dateEnd) + ' ' + DateUtils.formatAsDayMonth(dateEnd)
      } else if (dateStart) {
        return i18n.t('components.desktop-cards.notification-bar.next-booking') + ': ' + DateUtils.formatAsTime(dateStart) + ' ' + DateUtils.formatAsDayMonth(dateStart) + ' - ' + DateUtils.formatAsTime(dateEnd) + ' ' + DateUtils.formatAsDayMonth(dateEnd)
      } else {
        return i18n.t('components.desktop-cards.notification-bar.no-next-booking')
      }
    },
    chooseDesktop (template) {
      this.$snotify.clear()

      const yesAction = () => {
        this.$snotify.clear()
        const vgpus = (template.reservables && template.reservables.vgpus) || []
        if (vgpus.length) {
          this.checkCanStartNewNonpersistent({ templateId: template.id, profileIds: vgpus })
        } else {
          this.createDesktop({ template: template.id })
        }
      }

      const noAction = (toast) => {
        this.$snotify.clear()
      }

      this.$snotify.prompt(`${i18n.t('messages.confirmation.create-nonpersistent')}`, {
        position: 'centerTop',
        buttons: [
          { text: `${i18n.t('messages.yes')}`, action: yesAction, bold: true },
          { text: `${i18n.t('messages.no')}`, action: noAction }
        ],
        placeholder: ''
      })
    },
    isWaiting (interfaces) {
      return (this.waitingIp || this.desktopState === 'shutting-down') && DesktopUtils.networkNeedsIp(interfaces)
    },
    viewerNeedsIp (viewer) {
      return DesktopUtils.viewerNeedsIp(viewer)
    },
    onClickGoToNewTemplate (desktopId) {
      if (this.desktop.serverAutostart) {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.new-template-server'), '', true, 2000)
      } else if (this.desktopState === desktopStates.stopped) {
        this.goToNewTemplate(desktopId)
      } else {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.new-template-stop'), '', true, 2000)
      }
    },
    onClickGoToEditDesktop (payload) {
      if (this.desktop.server && this.desktopState !== desktopStates.failed) {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.edit-desktop-server'), '', true, 2000)
      } else if ([desktopStates.failed, desktopStates.stopped].includes(this.desktopState)) {
        this.goToEditDomain(payload.itemId)
      } else {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.edit-desktop-stop'), '', true, 2000)
      }
    },
    onClickDeleteDesktop (toast) {
      if ([desktopStates.failed, desktopStates.stopped].includes(this.desktopState)) {
        this.updateDesktopModal({
          show: true,
          type: 'delete',
          item: {
            id: this.desktop.id
          },
          tag: this.desktop.tag
        })
      } else {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.delete-desktop-stop'), '', true, 2000)
      }
    },
    onClickOpenDirectViewerModal () {
      this.fetchDirectLink(this.desktop.id)
    },
    onClickRecreateDesktop (payload) {
      if (this.desktopState === desktopStates.stopped) {
        this.$snotify.clear()

        const yesAction = () => {
          this.$snotify.clear()
          this.recreateDesktop({ id: payload.id })
        }

        const noAction = (toast) => {
          this.$snotify.clear()
        }

        this.$snotify.prompt(`${i18n.t('messages.confirmation.recreate-desktop', { name: payload.name })}`, {
          position: 'centerTop',
          buttons: [
            { text: `${i18n.t('messages.yes')}`, action: yesAction, bold: true },
            { text: `${i18n.t('messages.no')}`, action: noAction }
          ],
          placeholder: ''
        })
      } else {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('messages.info.recreate-desktop-stop'), '', true, 2000)
      }
    },
    onClickShowBastionModal (bastionItem) {
      this.bastionModalShow({ show: true, bastion: bastionItem, desktop: this.desktop })
    },
    onClickIncreaseStorage () {
      if (this.desktopState !== desktopStates.stopped) {
        ErrorUtils.showInfoMessage(this.$snotify, i18n.t('errors.increase_desktops_not_stopped'), '', true, 2000)
        return
      }
      const storageItem = this.getStorage.find(stg => stg.id === this.desktop.storage[0])
      if (storageItem) {
        this.showIncreaseModal({ item: storageItem, show: true })
      }
    }
  }
}
</script>
